import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, FileText, RefreshCw, Lock, AlertCircle, CheckCircle } from "lucide-react";

export default function BusinessPolicies() {
  const [activeSection, setActiveSection] = useState("terms");

  const sections = [
    { id: "terms", label: "Terms of Service", icon: FileText },
    { id: "privacy", label: "Privacy Policy", icon: Lock },
    { id: "refund", label: "Refund Policy", icon: RefreshCw },
    { id: "verification", label: "Verification Process", icon: Shield },
    { id: "guidelines", label: "Community Guidelines", icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12" data-testid="policies-header">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Business Policies
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Understanding our policies helps ensure a safe and transparent experience for all users on ReBusi.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6 bg-white border border-slate-100">
              <CardContent className="p-4">
                <nav className="space-y-2">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                          activeSection === section.id
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid={`nav-${section.id}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm">{section.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <Card className="bg-white border border-slate-100">
              <CardContent className="p-8">
                {activeSection === "terms" && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-slate-900">Terms of Service</h2>
                    <p className="text-slate-600">Last updated: March 28, 2026</p>
                    
                    <div className="space-y-4 text-slate-700 leading-relaxed">
                      <h3 className="text-xl font-semibold text-slate-900 mt-6">1. Acceptance of Terms</h3>
                      <p>
                        By accessing and using ReBusi, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use our services.
                      </p>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">2. Use of Services</h3>
                      <p>
                        ReBusi provides a platform for buying, selling businesses, and connecting with business consultants. Users must:
                      </p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Provide accurate and complete information</li>
                        <li>Maintain the security of their account credentials</li>
                        <li>Not engage in fraudulent or illegal activities</li>
                        <li>Respect intellectual property rights</li>
                        <li>Comply with all applicable laws and regulations</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">3. User Responsibilities</h3>
                      <p>
                        Users are responsible for all activities under their account. You agree to notify us immediately of any unauthorized use of your account.
                      </p>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">4. Business Listings</h3>
                      <p>
                        All business listings must be accurate and verified. Misrepresentation of business information may result in account suspension or termination.
                      </p>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">5. Limitation of Liability</h3>
                      <p>
                        ReBusi acts as an intermediary platform. We are not responsible for the accuracy of listings or the outcome of transactions between users.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === "privacy" && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-slate-900">Privacy Policy</h2>
                    <p className="text-slate-600">Last updated: March 28, 2026</p>
                    
                    <div className="space-y-4 text-slate-700 leading-relaxed">
                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Information We Collect</h3>
                      <p>We collect information that you provide directly to us, including:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Name, email address, and phone number</li>
                        <li>Business information and financial details (for listings)</li>
                        <li>Payment information (processed securely through Razorpay)</li>
                        <li>Communication history on the platform</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">How We Use Your Information</h3>
                      <p>We use collected information to:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Provide and maintain our services</li>
                        <li>Verify business listings and user identities</li>
                        <li>Process transactions and send notifications</li>
                        <li>Improve our platform and user experience</li>
                        <li>Prevent fraud and ensure platform security</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Data Security</h3>
                      <p>
                        We implement industry-standard security measures to protect your personal information. All sensitive data is encrypted in transit and at rest.
                      </p>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Data Sharing</h3>
                      <p>
                        We do not sell your personal information. We may share data with:
                      </p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Other users (as necessary for transactions)</li>
                        <li>Service providers (payment processors, hosting services)</li>
                        <li>Legal authorities (when required by law)</li>
                      </ul>
                    </div>
                  </div>
                )}

                {activeSection === "refund" && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-slate-900">Refund Policy</h2>
                    <p className="text-slate-600">Last updated: March 28, 2026</p>
                    
                    <div className="space-y-4 text-slate-700 leading-relaxed">
                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Consultation Services</h3>
                      <p>
                        Consultation booking payments are refundable under the following conditions:
                      </p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Cancellation 24 hours before scheduled time: 100% refund</li>
                        <li>Cancellation 12-24 hours before: 50% refund</li>
                        <li>Cancellation less than 12 hours: No refund</li>
                        <li>Consultant cancellation: Full refund + credit for next session</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Premium Listing Fees</h3>
                      <p>
                        Featured listing payments are non-refundable once the listing is published. However, if technical issues prevent your listing from being displayed, a full refund will be issued.
                      </p>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Refund Process</h3>
                      <p>
                        Approved refunds are processed within 5-7 business days to the original payment method.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === "verification" && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-slate-900">Verification Process</h2>
                    <p className="text-slate-600">Ensuring trust and authenticity</p>
                    
                    <div className="space-y-4 text-slate-700 leading-relaxed">
                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Business Listing Verification</h3>
                      <p>All business listings undergo a multi-step verification process:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Document verification (GST, business registration)</li>
                        <li>Financial statement review</li>
                        <li>Physical verification (for high-value listings)</li>
                        <li>Background check on business owners</li>
                        <li>Review by our verification team</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Consultant Verification</h3>
                      <p>Consultants must provide:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Professional credentials and certifications</li>
                        <li>Work experience documentation</li>
                        <li>Identity verification</li>
                        <li>Background check clearance</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Verified Badge</h3>
                      <p>
                        Listings and consultants that complete verification receive a "Verified" badge, indicating they have passed our authentication process.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === "guidelines" && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-slate-900">Community Guidelines</h2>
                    <p className="text-slate-600">Building a respectful community</p>
                    
                    <div className="space-y-4 text-slate-700 leading-relaxed">
                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Prohibited Activities</h3>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Posting false or misleading information</li>
                        <li>Harassment or abusive behavior</li>
                        <li>Spam or unsolicited marketing</li>
                        <li>Fraudulent transactions or scams</li>
                        <li>Violation of intellectual property rights</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Communication Standards</h3>
                      <p>When communicating on ReBusi:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Be professional and respectful</li>
                        <li>Provide accurate information</li>
                        <li>Respond to inquiries in a timely manner</li>
                        <li>Keep all business discussions on the platform</li>
                      </ul>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Reporting Violations</h3>
                      <p>
                        If you encounter violations of these guidelines, please report them to support@biznexus.com. We investigate all reports and take appropriate action.
                      </p>

                      <h3 className="text-xl font-semibold text-slate-900 mt-6">Consequences</h3>
                      <p>Violations may result in:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Warning and content removal</li>
                        <li>Temporary account suspension</li>
                        <li>Permanent account termination</li>
                        <li>Legal action (for severe violations)</li>
                      </ul>
                    </div>
                  </div>
                )}

                <div className="mt-12 p-6 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Questions About Our Policies?</h4>
                      <p className="text-slate-600 mb-3">
                        If you have any questions about these policies, please contact our support team.
                      </p>
                      <a href="/contact" className="text-blue-600 hover:text-blue-700 font-medium">
                        Contact Support →
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}